import React, { useEffect, useState } from 'react';
import { withTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import { Field, change } from 'redux-form';
import { Alert, Collapse } from 'reactstrap';
import HourField from '../../../shared/components/form/HourField';
import { SHIFT_ITEM_TYPE } from '../../../shared/utils/constants';

const ShiftItemsGrid = (props) => {
  const { t, formValues, dispatch, dto } = props;
  const [items, setItems] = useState([]);

  const changeInputValue = (evt, field, index) => {
    const { value } = evt.target;
    const updatedItems = [...items];
    updatedItems[index][field] = value;
    setItems(updatedItems);
  };

  const mergeItemsList = (listOfClasses, listOfIntervals) => {
    const mountedItems = [];
    let intervalIndex = 0;
    listOfClasses.forEach((item, itemIndex) => {
      mountedItems.push({
        ...item,
        displaySequence:
          item.id > 0 ? item.displaySequence : mountedItems.length + 1,
        sequence: itemIndex + 1,
      });
    });

    // Add the rest of intervals
    while (intervalIndex < listOfIntervals.length) {
      const currentInterval = listOfIntervals[intervalIndex];
      mountedItems.push({
        ...currentInterval,
        displaySequence:
          currentInterval.id > 0
            ? currentInterval.displaySequence
            : mountedItems.length + 1,
        sequence: intervalIndex + 1,
      });
      intervalIndex += 1;
    }
    const sortedMountedItems = mountedItems.sort((a, b) =>
      a.displaySequence > b.displaySequence ? 1 : -1
    );

    setItems(sortedMountedItems);
    dispatch(change('ShiftForm', 'items', mountedItems));
  };

  const createItems = () => {
    const { classQuantity, intervalQuantity } = formValues;

    const classes = [];
    const intervals = [];
    if (classQuantity > 0) {
      for (let classNum = 0; classNum < classQuantity; classNum += 1) {
        classes.push({
          type: SHIFT_ITEM_TYPE.CLASS,
          sequence: classNum,
          displaySequence: classNum,
        });
      }
    }

    if (intervalQuantity > 0) {
      for (
        let interValNum = 0;
        interValNum < intervalQuantity;
        interValNum += 1
      ) {
        intervals.push({
          type: SHIFT_ITEM_TYPE.INTERVAL,
          sequence: interValNum,
          displaySequence: interValNum,
        });
      }
    }

    mergeItemsList(classes, intervals);
  };

  const addOrRemoveItem = () => {
    const { classQuantity, intervalQuantity } = formValues;

    const classList = items.filter(
      (item) => item.type === SHIFT_ITEM_TYPE.CLASS
    );
    const intervalList = items.filter(
      (item) => item.type === SHIFT_ITEM_TYPE.INTERVAL
    );

    const newItem = {
      id: 0,
      startAt: '',
      endAt: '',
    };

    if (classQuantity > classList.length) {
      while (classList.length < classQuantity) {
        classList.push({
          ...newItem,
          sequence: classList.length,
          type: SHIFT_ITEM_TYPE.CLASS,
        });
      }
    } else {
      while (classList.length > classQuantity) {
        classList.pop();
      }
    }

    if (intervalQuantity > intervalList.length) {
      while (intervalQuantity > intervalList.length) {
        intervalList.push({
          ...newItem,
          sequence: intervalList.length,
          type: SHIFT_ITEM_TYPE.INTERVAL,
        });
      }
    } else {
      while (intervalList.length > intervalQuantity) {
        intervalList.pop();
      }
    }

    mergeItemsList(classList, intervalList);
  };

  const reorder = (list, startIndex, endIndex) => {
    const result = Array.from(list);
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);
    const cloneList = [...list];
    return result.map((item, index) => ({
      ...item,
      displaySequence: cloneList[index].displaySequence,
      startAt: cloneList[index].startAt,
      endAt: cloneList[index].endAt,
    }));
  };

  const onDragEnd = ({ destination, source }) => {
    if (destination) {
      const reordered = reorder(items, source.index, destination.index);
      setItems(reordered);
      dispatch(change('ShiftForm', 'items', reordered));
    }
  };

  useEffect(() => {
    if (formValues.id > 0 && formValues.shiftItems) {
      addOrRemoveItem();
    } else {
      createItems();
    }
  }, [formValues]);

  useEffect(() => {
    dispatch(change('ShiftForm', 'items', items));
  }, [items]);

  useEffect(() => {
    setItems(dto.shiftItems);
  }, [dto]);

  return (
    <div className="lesson-grid-container">
      <div className="lesson-grid-header">
        <div>
          <span>{t('general.single-class')}</span>
        </div>
        <div>
          <span>{t('general.start-at')}</span>
          <span>{t('general.end-at')}</span>
        </div>
      </div>
      {items.filter((item) => item.type === SHIFT_ITEM_TYPE.INTERVAL).length >
      0 ? (
        <Alert className="text-center" color="info">
          {t('general.drag-shift-items-instructions')}
        </Alert>
      ) : null}

      <DragDropContext onDragEnd={onDragEnd}>
        <Droppable droppableId="droppable">
          {(provided) => (
            <div {...provided.droppableProps} ref={provided.innerRef}>
              {items.map((item, index) => (
                <Draggable
                  key={`${item.type}-${item.displaySequence}`}
                  draggableId={`${item.type}-${item.displaySequence}`}
                  index={index}
                  isDragDisabled={item.type !== SHIFT_ITEM_TYPE.INTERVAL}
                >
                  {(providedx, snapshot) => (
                    <div
                      ref={providedx.innerRef}
                      {...providedx.draggableProps}
                      {...providedx.dragHandleProps}
                      className={
                        snapshot.isDragging
                          ? `lesson-grid-item is-dragging ${item.type}`
                          : `lesson-grid-item ${item.type}`
                      }
                    >
                      <div>
                        <span>
                          {item.type === SHIFT_ITEM_TYPE.CLASS
                            ? `${item.sequence}ª ${t('general.single-class')}`
                            : `${item.sequence}º ${t('general.interval')}`}
                        </span>
                      </div>
                      <div>
                        <Field
                          name={`startAt-${item.sequence}-${item.displaySequence}`}
                          component={HourField}
                          time={item.startAt}
                          value={item.startAt}
                          onChange={(evt) =>
                            changeInputValue(evt, 'startAt', index)
                          }
                        />
                        <Field
                          name={`endAt-${item.sequence}-${item.displaySequence}`}
                          component={HourField}
                          time={item.endAt}
                          value={item.endAt}
                          onChange={(evt) =>
                            changeInputValue(evt, 'endAt', index)
                          }
                        />
                      </div>
                    </div>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>
      <Field
        name="errorIntervalTime"
        component={(field) => (
          <Collapse
            isOpen={field.meta.touched && !!field.meta.error}
            className="width100 mt-2"
          >
            <Alert className="text-center" color="danger">
              {field.meta.error}
            </Alert>
          </Collapse>
        )}
      />
    </div>
  );
};

ShiftItemsGrid.propTypes = {
  t: PropTypes.func.isRequired,
  dispatch: PropTypes.func.isRequired,
  formValues: PropTypes.shape(PropTypes.shape).isRequired,
  dto: PropTypes.shape(PropTypes.shape).isRequired,
};

export default withTranslation('common')(ShiftItemsGrid);
